import type { FC } from 'react'

import { LoaderCircle } from '@skytech/ui'
import { useQuery } from '@tanstack/react-query'
import { twJoin } from 'tailwind-merge'

import queries from '@/api/queries'
import { useAppContext } from '@/App.context'
import { ecomPolicyResources } from '@/resources/ecom-policy'

import { usePolicyContext } from '../Policy.context'

import { ProductsSummary } from './ProductsSummary'
import { SummaryButtons } from './SummaryButtons'

import { usePolicyId } from '../../hooks/use-policy-id'

export const OrderSummary: FC<{
  isFloating?: boolean
  isAccessoriesVisible?: boolean
  isOnlyAccessoriesPolicy?: boolean
}> = ({ isFloating = false, isAccessoriesVisible = false, isOnlyAccessoriesPolicy = false }) => {
  const policyId = usePolicyId()
  const policyCtx = usePolicyContext()
  const { whiteLabel } = useAppContext()
  const [activeStep] = policyCtx.activeStep
  const [orderTotalCost] = policyCtx.orderTotalCost
  const [grossSalaryTaxBracket] = policyCtx.grossSalaryTaxBracket

  const { data, isLoading } = useQuery(queries.policy(policyId), () =>
    ecomPolicyResources.get(policyId)
  )

  if (isLoading) return <LoaderCircle />
  if (!data) return null

  const isOnlyProductView = Boolean(data.isLocked && data.isProductViewAllowedWhenLocked)

  const isSpendingLimitExceeded = orderTotalCost > data.maximumAmount
  const isSummaryButtonsVisible =
    activeStep === 'PRODUCT_SELECT' ||
    activeStep === 'ACCESSORIES' ||
    activeStep === 'GREENPOOL_PRODUCT_SELECT'

  return (
    <div
      className={twJoin(
        'z-50 flex flex-col gap-1 bg-white',
        // Adjust margin to ensure full width alignment with the container having id="base-container"
        isFloating ? 'max-w-screen-xl' : '-mx-2 sm:-mx-4 md:mx-0 lg:max-w-screen-2xl',
        isFloating && 'shadow-basic-black fixed bottom-0 xl:rounded-3xl',
        isFloating &&
          `left-0 max-w-screen-xl max-lg:right-0 lg:left-56 lg:w-[calc(100%-14.5rem)] xl:left-80 xl:w-[calc(100%-26rem)]`,
        isFloating &&
          'p-2 md:p-3 lg:mx-2 lg:pl-10 xl:mx-4 xl:mb-4 xl:py-4 xl:pl-6 xl:pr-10 2xl:mx-8',
        whiteLabel
      )}
    >
      {!isOnlyProductView && activeStep !== 'GREENPOOL_PRODUCT_SELECT' && (
        <ProductsSummary
          maxAmount={data.maximumAmount}
          coverAmount={data.coverAmount}
          coverUsed={data.coverUsed}
          paymentInterval={data.paymentInterval}
          orderTotalCost={orderTotalCost}
          displayMode={grossSalaryTaxBracket ? 'grossSalaryScheme' : 'default'}
        />
      )}
      {isSummaryButtonsVisible && (
        <SummaryButtons
          isGreenpoolAvailable={Boolean(data.greenpoolTypeId) && data.greenpoolAssets.length > 0}
          isOnlyAccessoriesPolicy={isOnlyAccessoriesPolicy}
          isAccessoriesVisible={isAccessoriesVisible}
          isSpendingLimitExceeded={isSpendingLimitExceeded}
        />
      )}
    </div>
  )
}
